import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { BrandMark } from '../components/BrandMark';

type Phase = 'form' | 'sending' | 'pending' | 'approved' | 'rejected' | 'active' | 'invalid';

export function GarcomPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('form');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const reqIdRef = useRef<string | null>(null);

  // garante sessão anônima (mesmo padrão do diner)
  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) await supabase.auth.signInAnonymously();
    })();
  }, []);

  async function request(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return setPhase('invalid');
    setPhase('sending');
    const { data, error } = await supabase.rpc('staff_request_access', { p_token: token, p_name: name, p_phone: phone });
    if (error) {
      setPhase(/inv[aá]lid/i.test(error.message) ? 'invalid' : 'form');
      return;
    }
    const res = data as { status: string; request_id?: string };
    if (res.status === 'active') return setPhase('active');
    reqIdRef.current = res.request_id ?? null;
    setPhase('pending');
  }

  // enquanto pendente, acompanha a decisão do responsável
  useEffect(() => {
    if (phase !== 'pending' || !reqIdRef.current) return;
    const check = async () => {
      const { data } = await supabase.from('staff_access_requests').select('status').eq('id', reqIdRef.current as string).maybeSingle();
      const s = (data as { status?: string } | null)?.status;
      if (s === 'approved') setPhase('approved');
      else if (s === 'rejected') setPhase('rejected');
    };
    void check();
    const id = window.setInterval(() => void check(), 4000);
    return () => window.clearInterval(id);
  }, [phase]);

  return (
    <div className="diner">
      <header className="diner__header">
        <span className="brand">
          <BrandMark className="brand__mark" />
          <span className="brand__name">{t('staff.title')}</span>
        </span>
        <LanguageSwitcher />
      </header>

      <main className="diner__main">
        <div className="auth-card">
          {phase === 'form' || phase === 'sending' ? (
            <>
              <h1 className="auth-card__title">{t('staff.title')}</h1>
              <p className="auth-card__subtitle">{t('staff.intro')}</p>
              <form onSubmit={request} className="form">
                <label className="field">
                  <span>{t('staff.name')}</span>
                  <input required value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="field">
                  <span>{t('staff.phone')}</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
                </label>
                <button type="submit" className="btn btn--primary btn--block" disabled={phase === 'sending' || !name.trim()}>
                  {phase === 'sending' ? t('staff.requesting') : t('staff.request')}
                </button>
              </form>
            </>
          ) : phase === 'pending' ? (
            <div className="center-note">
              <div className="spinner-dot" />
              <p>{t('staff.pending')}</p>
            </div>
          ) : phase === 'approved' ? (
            <div className="center-note">
              <h1 className="auth-card__title">{t('staff.approved')}</h1>
              <a className="btn btn--primary btn--block" href="/app">{t('staff.go_app')}</a>
            </div>
          ) : phase === 'active' ? (
            <div className="center-note">
              <p>{t('staff.active')}</p>
              <a className="btn btn--primary btn--block" href="/app">{t('staff.go_app')}</a>
            </div>
          ) : phase === 'rejected' ? (
            <p className="center-note">{t('staff.rejected')}</p>
          ) : (
            <p className="center-note">{t('staff.invalid')}</p>
          )}
        </div>
      </main>
    </div>
  );
}
